import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Download, RotateCcw, FileText, Eye, EyeOff, Plus, Trash2, Save, CheckCircle, List } from 'lucide-react';
import { fetchProposalProducts, fetchProposalSettings, seedProposalCatalog } from '../actions/proposalCatalog';
import { createProposal, updateProposal } from '../actions/proposals';
import {
  LICENSE_TYPES, MAINTENANCE_TYPES, ZONES, INSTALLATION_TYPES, LOCK_TYPES,
  SALE_MODES, OPERATING_MODES, fmt, fmtAlways, fmtPct
} from '../utils/proposalCatalog';
import VisualLockerLine, { getModuleImage, getModuleImageHeight, getModuleBottomOffset } from '../components/VisualLockerLine';
import { withRouter } from '../utils/withRouter';
import '../styles/ProposalConfiguratorView.css';

// ─── PDF English Label Maps ───────────────────────────────
const SALE_MODE_EN = { purchase: 'Purchase', rental: 'Rental', revenue_share: 'Revenue Share' };
const LICENSE_EN   = { BASIC: 'BASIC', PRO: 'PRO', NONE: 'No License' };
const MAINT_EN     = { NONE: 'No Maintenance', ESTANDAR: 'STANDARD', PREMIUM: 'PREMIUM' };

// Decimal-aware formatter for fractional prices (consumables)
const fmtDec = (n) => {
  if (n == null || isNaN(n)) return '0,00 €';
  return n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
};

// ─── Design Tokens ─────────────────────────────────────────
const TEAL      = '#0a4080';
const DARK      = '#001d3a';
const BG        = '#f4f6f9';
const CARD      = '#ffffff';
const BORDER    = '#e2e8f0';
const TEXT_1    = '#1e293b';
const TEXT_2    = '#64748b';
const TEXT_3    = '#94a3b8';
const GREEN     = '#10b981';
const AMBER     = '#f59e0b';
const RED       = '#ef4444';

const TH_BG    = '#f0fdfa';
const TH_COLOR = '#0e7490';
const ROW_HL   = '#f0f9ff';

const cardStyle    = { background: CARD, borderRadius: 12, border: `1px solid ${BORDER}`, padding: 24 };
const labelStyle   = { fontSize: 12, fontWeight: 600, color: TEXT_2, marginBottom: 4, display: 'block' };
const inputStyle   = { width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 14, color: TEXT_1, outline: 'none', boxSizing: 'border-box' };
const selectStyle  = { ...inputStyle, background: '#fff', cursor: 'pointer' };
const sectionTitle = { fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: TEXT_2, marginBottom: 16 };

class ProposalConfiguratorView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      clientName: '',
      clientCompany: '',
      clientCIF: '',
      clientAddress: '',
      proposalRef: '',
      proposalDate: new Date().toISOString().slice(0, 10),
      notes: '',
      licenseType: 'BASIC',
      maintenanceType: 'NONE',
      zone: 'ZONA_I',
      lockerRange: '0-500',
      customization: false,
      installationType: 'interior',
      catalogTab: 'interior',
      lockType: 'STANDARD',
      saleMode: 'purchase',
      operatingMode: '24H',
      onlineSale: false,
      productMode: 'multi',
      includedDevices: [],
      contractDuration: 60,
      depositMonths: 4,
      commissionPcts: [],
      hideMinAnnualCommission: false,
      installationCost: 0,
      conditions: '',
      discountProduct: 0,
      discountMaintenance: 0,
      discountLicense: 0,
      discountRental: 0,
      cabinetItems: [],
      posItems: [],
      consumableItems: [],
      configLine: [],
      moduleMode: 'visual',
      selectedCabinetCode: '',
      selectedPosCode: '',
      selectedConsumableCode: '',
      generatingPdf: false,
      generatingPdfEn: false,
      exportLang: 'es',
      showPreview: false,
      savedProposalId: null,
      savingProposal: false,
      saveSuccess: false,
    };
    this.printRef       = React.createRef();
    this.printCoverRef  = React.createRef();
    this.printVisualRef = React.createRef();
    this.printDataRef   = React.createRef();
    this.printOfertaRef = React.createRef();
  }

  componentDidMount() {
    this.props.fetchProposalProducts();
    this.props.fetchProposalSettings();
    const loaded = this.props.location && this.props.location.state && this.props.location.state.loadedProposal;
    if (loaded) {
      this.loadProposal(loaded);
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.products && this.props.products.length === 0 && prevProps.products === null) {
      this.props.seedProposalCatalog().then(() => this.props.fetchProposalProducts());
    }
    // Sync consumable unitPrices with the live catalog whenever products are refreshed
    if (this.props.products && this.props.products !== prevProps.products) {
      this.setState(prev => {
        if (!prev.consumableItems || prev.consumableItems.length === 0) return null;
        const updated = prev.consumableItems.map(it => {
          const product = this.props.products.find(p => p.code === it.code);
          return product ? { ...it, unitPrice: product.priceInterior || 0 } : it;
        });
        // Only update state if at least one price changed
        const changed = updated.some((it, i) => it.unitPrice !== prev.consumableItems[i].unitPrice);
        return changed ? { consumableItems: updated } : null;
      });
    }
  }

  getProducts(category) {
    const { products } = this.props;
    if (!products) return [];
    return products.filter(p => p.category === category);
  }

  getSetting(path, fallback) {
    const { settings } = this.props;
    if (!settings) return fallback;
    const parts = path.split('.');
    let v = settings;
    for (const k of parts) {
      if (v == null) return fallback;
      v = v[k];
    }
    return v ?? fallback;
  }

  getLicensePricePerPOS() {
    const { licenseType } = this.state;
    if (licenseType === 'NONE') return 0;
    return this.getSetting(`licensePricing.${licenseType}.perPOS`, licenseType === 'PRO' ? 135 : 99);
  }

  getLockerRangeKey(_totalLockers) {
    return 'default';
  }

  getMaintenanceCost(totalLockers) {
    const { maintenanceType, zone } = this.state;
    if (maintenanceType === 'NONE') return 0;
    const rangeKey = this.getLockerRangeKey(totalLockers);
    const zoneData = this.getSetting(`maintenancePricing.${maintenanceType}.${zone}`, null);
    if (!zoneData) return 0;
    if (typeof zoneData === 'number') return zoneData;
    if (zoneData[rangeKey] != null) return zoneData[rangeKey];
    const firstKey = Object.keys(zoneData)[0];
    return firstKey ? (zoneData[firstKey] || 0) : 0;
  }

  priceField(product) {
    return product.priceInterior;
  }

  rentField(product) {
    return product.rentInterior;
  }

  getConsumableUnitPrice(item) {
    const product = (this.props.products || []).find(p => p.code === item.code);
    return product ? (product.priceInterior || 0) : (item.unitPrice || 0);
  }

  addItem = (category) => {
    const code = category === 'cabinet' ? this.state.selectedCabinetCode : this.state.selectedPosCode;
    if (!code) return;
    const product = (this.props.products || []).find(p => p.code === code);
    if (!product) return;

    const item = {
      productId: product._id,
      code: product.code,
      envCode: product.code,
      name: product.name,
      lockers: 0,
      unitPrice: this.priceField(product),
      unitRent: this.rentField(product),
      personalizationPrice: product.personalizationPrice || 0,
      qty: 1,
    };

    if (category === 'cabinet') {
      this.setState(prev => ({ cabinetItems: [...prev.cabinetItems, item], selectedCabinetCode: '' }));
    } else {
      this.setState(prev => ({ posItems: [...prev.posItems, item], selectedPosCode: '' }));
    }
  };

  removeItem = (category, index) => {
    if (category === 'cabinet') {
      this.setState(prev => ({ cabinetItems: prev.cabinetItems.filter((_, i) => i !== index) }));
    } else {
      this.setState(prev => ({ posItems: prev.posItems.filter((_, i) => i !== index) }));
    }
  };

  setItemQty = (category, index) => (e) => {
    const v = Math.max(1, parseInt(e.target.value) || 1);
    if (category === 'cabinet') {
      this.setState(prev => {
        const items = [...prev.cabinetItems];
        items[index] = { ...items[index], qty: v };
        return { cabinetItems: items };
      });
    } else {
      this.setState(prev => {
        const items = [...prev.posItems];
        items[index] = { ...items[index], qty: v };
        return { posItems: items };
      });
    }
  };

  addConsumable = () => {
    const { selectedConsumableCode } = this.state;
    if (!selectedConsumableCode) return;
    const product = (this.props.products || []).find(p => p.code === selectedConsumableCode);
    if (!product) return;
    const item = {
      productId: product._id,
      code: product.code,
      name: product.name,
      image: product.image || product._image || '',
      unitPrice: product.priceInterior || 0,
      qty: 1,
    };
    this.setState(prev => ({ consumableItems: [...prev.consumableItems, item], selectedConsumableCode: '' }));
  };

  removeConsumable = (index) => {
    this.setState(prev => ({ consumableItems: prev.consumableItems.filter((_, i) => i !== index) }));
  };

  setConsumableQty = (index) => (e) => {
    const v = Math.max(1, parseInt(e.target.value) || 1);
    this.setState(prev => {
      const items = [...prev.consumableItems];
      items[index] = { ...items[index], qty: v };
      return { consumableItems: items };
    });
  };

  onVisualLineChange = (line) => {
    const { products } = this.props;
    const counts = {};
    line.forEach(m => { counts[m.code] = (counts[m.code] || 0) + 1; });
    const buildItems = (category) => Object.entries(counts).map(([code, qty]) => {
      const prod = (products || []).find(p => p.code === code && p.category === category);
      if (!prod) return null;
      return {
        productId: prod._id, code, envCode: code,
        name: prod.name, lockers: 0, category: prod.category,
        unitPrice: prod.priceInterior,
        unitRent: prod.rentInterior,
        personalizationPrice: prod.personalizationPrice || 0,
        qty,
      };
    }).filter(Boolean);
    this.setState({ configLine: line, cabinetItems: buildItems('cabinet'), posItems: buildItems('pos') });
  };

  handleInstallationChange = (_tabValue) => {};

  loadProposal = (p) => {
    this.setState({
      savedProposalId: p._id || null,
      clientName:    p.clientName    || '',
      clientCompany: p.clientCompany || '',
      clientCIF:     p.clientCIF     || '',
      clientAddress: p.clientAddress || '',
      proposalRef:   p.proposalRef   || '',
      proposalDate:  p.proposalDate  || new Date().toISOString().slice(0, 10),
      notes:         p.notes         || '',
      conditions:    p.conditions    || '',
      licenseType:     p.licenseType     || 'PRO',
      maintenanceType: p.maintenanceType || 'NONE',
      zone:            p.zone            || 'ZONA_I',
      lockerRange:     p.lockerRange     || '0-500',
      customization:   p.customization !== undefined ? p.customization : true,
      installationType: p.installationType || 'interior',
      catalogTab:      p.installationType || 'interior',
      lockType:        p.lockType   || 'STANDARD',
      saleMode:        p.saleMode   || 'purchase',
      operatingMode:   p.operatingMode || '24H',
      onlineSale:      p.onlineSale !== undefined ? p.onlineSale : false,
      productMode:     p.productMode || 'multi',
      includedDevices: Array.isArray(p.includedDevices) ? p.includedDevices : [],
      moduleMode:      p.moduleMode || 'visual',
      contractDuration:    p.contractDuration    !== undefined ? p.contractDuration    : 60,
      depositMonths:       p.depositMonths       !== undefined ? p.depositMonths       : 4,
      commissionPcts: Array.isArray(p.commissionPcts) ? p.commissionPcts
        : Array.from({ length: Math.round((p.contractDuration || 60) / 12) }, () => p.commissionPct || 0),
      hideMinAnnualCommission: p.hideMinAnnualCommission !== undefined ? p.hideMinAnnualCommission : false,
      installationCost:    parseFloat(p.installationCost) || 0,
      discountProduct:     p.discountProduct     !== undefined ? p.discountProduct     : 0,
      discountMaintenance: p.discountMaintenance !== undefined ? p.discountMaintenance : 0,
      discountLicense:     p.discountLicense     !== undefined ? p.discountLicense     : 0,
      discountRental:      p.discountRental      !== undefined ? p.discountRental      : 0,
      cabinetItems:    p.cabinetItems    || [],
      posItems:        p.posItems        || [],
      consumableItems: p.consumableItems || [],
      configLine:      p.configLine      || [],
    });
  };

  gatherProposalData = () => {
    const s = this.state;
    return {
      clientName:    s.clientName,
      clientCompany: s.clientCompany,
      clientCIF:     s.clientCIF,
      clientAddress: s.clientAddress,
      proposalRef:   s.proposalRef,
      proposalDate:  s.proposalDate,
      notes:         s.notes,
      conditions:    s.conditions,
      licenseType:     s.licenseType,
      maintenanceType: s.maintenanceType,
      zone:            s.zone,
      lockerRange:     s.lockerRange,
      customization:   s.customization,
      installationType: s.installationType,
      lockType:        s.lockType,
      saleMode:        s.saleMode,
      operatingMode:   s.operatingMode,
      onlineSale:      s.onlineSale,
      productMode:     s.productMode,
      includedDevices: s.includedDevices,
      moduleMode:      s.moduleMode,
      contractDuration:    s.contractDuration,
      depositMonths:       s.depositMonths,
      commissionPcts:      s.commissionPcts,
      hideMinAnnualCommission: s.hideMinAnnualCommission,
      installationCost:    s.installationCost,
      discountProduct:     s.discountProduct,
      discountMaintenance: s.discountMaintenance,
      discountLicense:     s.discountLicense,
      discountRental:      s.discountRental,
      cabinetItems:    s.cabinetItems,
      posItems:        s.posItems,
      consumableItems: s.consumableItems,
      configLine:      s.configLine,
      createdByName: this.props.fullname || '',
      createdById:   this.props.authId   || '',
    };
  };

  saveProposal = async () => {
    const { savedProposalId } = this.state;
    this.setState({ savingProposal: true, saveSuccess: false });
    try {
      const data = this.gatherProposalData();
      if (savedProposalId) {
        await this.props.updateProposal(savedProposalId, data);
      } else {
        const result = await this.props.createProposal(data);
        if (result && result.response && result.response._id) {
          this.setState({ savedProposalId: result.response._id });
        }
      }
      this.setState({ saveSuccess: true });
      setTimeout(() => this.setState({ saveSuccess: false }), 3000);
    } catch (err) {
      console.error('Error saving proposal:', err);
    } finally {
      this.setState({ savingProposal: false });
    }
  };

  set = (field) => (e) => this.setState({ [field]: e.target.value });

  setNum = (field) => (e) => {
    const v = parseFloat(e.target.value);
    this.setState({ [field]: isNaN(v) ? 0 : Math.max(0, Math.min(100, v)) });
  };

  setDiscount = (field) => (e) => {
    const v = parseFloat(e.target.value);
    this.setState({ [field]: isNaN(v) ? 0 : v });
  };

  handleSaleModeChange = (e) => {
    const saleMode = e.target.value;
    const update = { saleMode };
    if (saleMode === 'revenue_share') {
      if (this.state.licenseType === 'NONE') update.licenseType = 'BASIC';
    }
    this.setState(update);
  };

  resetForm = () => {
    this.setState({
      clientName: '', clientCompany: '', clientCIF: '', clientAddress: '',
      proposalRef: '', notes: '',
      proposalDate: new Date().toISOString().slice(0, 10),
      licenseType: 'BASIC', maintenanceType: 'NONE', zone: 'ZONA_I',
      lockerRange: '0-500', customization: true, installationType: 'interior',
      lockType: 'STANDARD', saleMode: 'purchase',
      productMode: 'multi', includedDevices: [],
      contractDuration: 60, depositMonths: 4, commissionPcts: [], hideMinAnnualCommission: false,
      installationCost: 0, conditions: '',
      discountProduct: 0, discountMaintenance: 0, discountLicense: 0, discountRental: 0,
      cabinetItems: [], posItems: [], consumableItems: [],
      selectedCabinetCode: '', selectedPosCode: '', selectedConsumableCode: '',
      configLine: [], catalogTab: 'interior', moduleMode: 'visual',
    });
  };

  setModuleMode = (mode) => {
    if (mode === this.state.moduleMode) return;
    if (mode === 'visual') {
      this.setState({ moduleMode: mode, configLine: [], cabinetItems: [], posItems: [], catalogTab: this.state.installationType });
    } else {
      this.setState({ moduleMode: mode, configLine: [] });
    }
  };

  calculateTotals = () => {
    const { cabinetItems, posItems, consumableItems, customization, saleMode,
            discountProduct, discountMaintenance, discountLicense,
            contractDuration, depositMonths } = this.state;

    const sumItems = (items) => items.reduce((acc, it) => ({
      units: acc.units + it.qty,
      lockers: acc.lockers + it.qty * (it.lockers || 0),
      sale: acc.sale + it.qty * it.unitPrice,
      rent: acc.rent + it.qty * it.unitRent,
    }), { units: 0, lockers: 0, sale: 0, rent: 0 });

    const cab = sumItems(cabinetItems);
    const pos = sumItems(posItems);
    const hasMachines = cabinetItems.length > 0 || posItems.length > 0;

    const consumableSale = (consumableItems || []).reduce((s, it) => s + it.qty * this.getConsumableUnitPrice(it), 0);

    const totalLockers = cab.lockers + pos.lockers;
    const baseSale     = cab.sale + pos.sale;
    const baseRent     = cab.rent + pos.rent;

    const licenseMonthlyPerPOS = hasMachines ? this.getLicensePricePerPOS() : 0;
    const totalPOS         = cab.units;
    const baseLicenseMonth = hasMachines ? Math.round(totalPOS * licenseMonthlyPerPOS) : 0;
    const baseMaintYear    = hasMachines ? this.getMaintenanceCost(totalLockers) : 0;

    const dp = 1 - discountProduct / 100;
    const dm = 1 - discountMaintenance / 100;
    const dl = 1 - discountLicense / 100;

    const sale              = Math.round(baseSale * dp);
    const licenseMonthly    = Math.round(baseLicenseMonth * dl);
    const licenseAnnual     = licenseMonthly * 12;
    const maintenanceAnnual = Math.round(baseMaintYear * dm);

    const rentalYears       = contractDuration / 12;
    const rentAnnual        = Math.round(sale * 0.075 + sale / rentalYears + licenseAnnual + maintenanceAnnual);
    const rentMonthly       = Math.round(rentAnnual / 12);
    const baseRentAnnual    = Math.round(baseSale * 0.075 + baseSale / rentalYears + baseLicenseMonth * 12 + baseMaintYear);
    const rentalDiscountPct = baseRentAnnual > 0 ? Math.round((1 - rentAnnual / baseRentAnnual) * 100) : 0;

    const personalization   = customization ? [...cabinetItems, ...posItems].reduce((s, it) => {
      return s + it.qty * (it.personalizationPrice || 0);
    }, 0) : 0;
    const deposit           = rentMonthly * depositMonths;

    return {
      totalCabinets: cab.units, totalKiosks: pos.units, totalLockers,
      totalUnits: cab.units + pos.units,
      totalModules: cab.units + pos.units,
      sale, licenseMonthly, licenseAnnual, maintenanceAnnual,
      rentAnnual, rentMonthly, baseRentAnnual, rentalDiscountPct,
      personalization, deposit,
      cabinetSale: cab.sale, cabinetRent: cab.rent, cabinetUnits: cab.units, cabinetLockers: cab.lockers,
      posSale: pos.sale, posRent: pos.rent, posUnits: pos.units,
      baseSale, baseLicenseMonth, baseMaintYear, baseRent,
      consumableSale, hasMachines,
    };
  };

  savePdfEn = async () => {
    await new Promise(resolve => this.setState({ exportLang: 'en', generatingPdfEn: true }, resolve));
    const coverEl  = this.printCoverRef.current;
    const visualEl = this.printVisualRef.current;
    const dataEl   = this.printDataRef.current;
    const ofertaEl = this.printOfertaRef.current;
    if (!coverEl || !visualEl || !dataEl || !ofertaEl) {
      this.setState({ exportLang: 'es', generatingPdfEn: false });
      return;
    }
    try {
      const PORTRAIT  = { w: 210, h: 297 };
      const LANDSCAPE = { w: 297, h: 210 };
      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
      const renderNode = async (el, orientation, isFirstPage) => {
        const page = orientation === 'l' ? LANDSCAPE : PORTRAIT;
        const canvas = await html2canvas(el, { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff' });
        const imgData = canvas.toDataURL('image/jpeg', 1.0);
        const imgW = page.w;
        const imgH = (canvas.height * imgW) / canvas.width;
        if (!isFirstPage) pdf.addPage('a4', orientation);
        if (imgH <= page.h) {
          pdf.addImage(imgData, 'JPEG', 0, 0, imgW, imgH, undefined, 'NONE');
        } else {
          let pos = 0;
          pdf.addImage(imgData, 'JPEG', 0, pos, imgW, imgH, undefined, 'NONE');
          let left = imgH - page.h;
          while (left > 0) {
            pos -= page.h;
            pdf.addPage('a4', orientation);
            pdf.addImage(imgData, 'JPEG', 0, pos, imgW, imgH, undefined, 'NONE');
            left -= page.h;
          }
        }
      };
      await renderNode(coverEl, 'p', true);
      await renderNode(dataEl, 'p', false);
      await renderNode(ofertaEl, 'p', false);
      if (this.state.moduleMode === 'visual') {
        await renderNode(visualEl, 'l', false);
      }
      const ref  = this.state.proposalRef || 'proposal';
      const date = this.state.proposalDate.replace(/-/g, '');
      pdf.save(`VACWAY_Proposal_${ref}_${date}.pdf`);
    } catch (err) {
      console.error('PDF generation error (EN):', err);
    } finally {
      await new Promise(resolve => this.setState({ exportLang: 'es', generatingPdfEn: false }, resolve));
    }
  };

  savePdf = async () => {
    const coverEl  = this.printCoverRef.current;
    const visualEl = this.printVisualRef.current;
    const dataEl   = this.printDataRef.current;
    const ofertaEl = this.printOfertaRef.current;
    if (!coverEl || !visualEl || !dataEl) return;
    this.setState({ generatingPdf: true });
    try {
      const PORTRAIT  = { w: 210, h: 297 };
      const LANDSCAPE = { w: 297, h: 210 };

      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });

      const renderNode = async (el, orientation, isFirstPage) => {
        const page = orientation === 'l' ? LANDSCAPE : PORTRAIT;
        const canvas = await html2canvas(el, {
          scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff',
        });
        const imgData = canvas.toDataURL('image/jpeg', 1.0);
        const imgW = page.w;
        const imgH = (canvas.height * imgW) / canvas.width;

        if (!isFirstPage) pdf.addPage('a4', orientation);

        if (imgH <= page.h) {
          pdf.addImage(imgData, 'JPEG', 0, 0, imgW, imgH, undefined, 'NONE');
        } else {
          let pos = 0;
          pdf.addImage(imgData, 'JPEG', 0, pos, imgW, imgH, undefined, 'NONE');
          let left = imgH - page.h;
          while (left > 0) {
            pos -= page.h;
            pdf.addPage('a4', orientation);
            pdf.addImage(imgData, 'JPEG', 0, pos, imgW, imgH, undefined, 'NONE');
            left -= page.h;
          }
        }
      };

      await renderNode(coverEl, 'p', true);
      await renderNode(dataEl, 'p', false);
      await renderNode(ofertaEl, 'p', false);
      if (this.state.moduleMode === 'visual') {
        await renderNode(visualEl, 'l', false);
      }

      const ref  = this.state.proposalRef || 'propuesta';
      const date = this.state.proposalDate.replace(/-/g, '');
      pdf.save(`VACWAY_Propuesta_${ref}_${date}.pdf`);
    } catch (err) {
      console.error('PDF generation error:', err);
    } finally {
      this.setState({ generatingPdf: false });
    }
  };

  renderVisualItemList() {
    const { cabinetItems, posItems, saleMode } = this.state;
    const allItems = [...cabinetItems, ...posItems];
    if (allItems.length === 0) return null;
    const showSale = saleMode === 'purchase';
    const priceLabel = showSale ? 'Precio' : 'Alq./mes';
    const thS = { padding: '8px 10px', textAlign: 'right', fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: TH_COLOR, background: TH_BG, borderBottom: `2px solid ${BORDER}`, whiteSpace: 'nowrap' };
    const tdS = { padding: '7px 10px', textAlign: 'right', borderBottom: `1px solid #f1f5f9`, fontSize: 13, color: TEXT_1 };
    const total = allItems.reduce((a, it) => ({
      units: a.units + it.qty,
      sale: a.sale + it.qty * it.unitPrice, rent: a.rent + it.qty * it.unitRent,
    }), { units: 0, sale: 0, rent: 0 });
    return (
      <div style={{ borderTop: `1px solid ${BORDER}` }}>
        <div style={{ padding: '12px 24px 8px' }}>
          <div style={sectionTitle}>Productos configurados</div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
            <thead>
              <tr>
                <th style={{ ...thS, textAlign: 'left', minWidth: 200 }}>Producto</th>
                <th style={{ ...thS, minWidth: 70 }}>Tipo</th>
                <th style={{ ...thS, minWidth: 80 }}>{priceLabel}</th>
                <th style={{ ...thS, minWidth: 60 }}>Cant.</th>
                <th style={{ ...thS, minWidth: 85 }}>{showSale ? 'Total Venta' : 'Total Alq.'}</th>
              </tr>
            </thead>
            <tbody>
              {allItems.map((it, idx) => (
                <tr key={idx} style={{ background: idx % 2 === 0 ? ROW_HL : '#fff' }}>
                  <td style={{ ...tdS, textAlign: 'left', fontWeight: 600 }}>{it.name} <span style={{ fontSize: 10, color: TEXT_2, fontWeight: 400 }}>({it.code})</span></td>
                  <td style={{ ...tdS, textAlign: 'left' }}>
                    <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 10, fontWeight: 700, background: '#e0f2fe', color: '#0369a1' }}>
                      Máquina
                    </span>
                  </td>
                  <td style={tdS}>{fmtAlways(showSale ? it.unitPrice : it.unitRent)}</td>
                  <td style={{ ...tdS, fontWeight: 600 }}>{it.qty}</td>
                  <td style={{ ...tdS, fontWeight: 600, color: showSale ? TEAL : GREEN }}>{fmtAlways(it.qty * (showSale ? it.unitPrice : it.unitRent))}</td>
                </tr>
              ))}
              <tr style={{ background: '#f8fafc' }}>
                <td style={{ ...tdS, textAlign: 'left', fontWeight: 700, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}>Total</td>
                <td style={{ ...tdS, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}></td>
                <td style={{ ...tdS, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}></td>
                <td style={{ ...tdS, fontWeight: 700, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}>{total.units}</td>
                <td style={{ ...tdS, fontWeight: 700, borderTop: `2px solid ${BORDER}`, borderBottom: 'none', color: showSale ? TEAL : GREEN }}>{fmtAlways(showSale ? total.sale : total.rent)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  renderVisualSection() {
    const { products } = this.props;
    const { configLine, moduleMode } = this.state;
    const cabinets    = this.getProducts('cabinet');

    const modeSwitch = (
      <div style={{ padding: '14px 24px 12px', borderBottom: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={sectionTitle}>Vista de Instalación</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: TEXT_2 }}>Modo:</span>
          {[{ v: 'visual', label: 'Visual' }, { v: 'list', label: 'Lista' }].map(({ v, label }) => (
            <button key={v} type="button" onClick={() => this.setModuleMode(v)}
              style={{
                padding: '4px 14px', fontSize: 12, fontWeight: 600, border: 'none', borderRadius: 6,
                cursor: 'pointer', transition: 'all 0.15s',
                background: moduleMode === v ? 'linear-gradient(135deg, #022040 0%, #0a4080 100%)' : '#f1f5f9',
                color: moduleMode === v ? '#fff' : TEXT_2,
              }}>
              {label}
            </button>
          ))}
        </div>
      </div>
    );

    if (moduleMode === 'list') {
      return (
        <div>
          <div style={{ ...cardStyle, marginBottom: 20, padding: 0, overflow: 'hidden' }}>
            {modeSwitch}
          </div>
          {this.renderProductSection('Máquinas', 'cabinet', this.state.cabinetItems, cabinets)}
        </div>
      );
    }

    return (
      <div style={{ ...cardStyle, marginBottom: 20, padding: 0, overflow: 'hidden' }}>
        {modeSwitch}
        <div style={{ padding: 20 }}>
          <VisualLockerLine
            products={products || []}
            env="interior"
            lockedEnv={null}
            configLine={configLine}
            onChange={this.onVisualLineChange}
            consumableItems={this.state.consumableItems || []}
          />
        </div>
        {configLine.length > 0 && (
          <div style={{ padding: '8px 24px 14px', borderTop: `1px solid ${BORDER}`, fontSize: 12, color: TEXT_2 }}>
            <strong>{[...this.state.cabinetItems, ...this.state.posItems].reduce((s, it) => s + it.qty, 0)}</strong> productos configurados
          </div>
        )}
        {this.renderVisualItemList()}
      </div>
    );
  }

  renderPrintVisualLine() {
    const { configLine, consumableItems } = this.state;
    const hasConsumables = consumableItems && consumableItems.length > 0;
    if ((!configLine || configLine.length === 0) && !hasConsumables) return null;

    const IMG_W = 420;
    const SCALE = 680 / 200;

    // Deduplicate machines by type
    const machineGroups = {};
    (configLine || []).forEach(m => {
      const key = (m.image || m._image || m.name || 'unknown').toLowerCase();
      if (!machineGroups[key]) machineGroups[key] = { machine: m, count: 0 };
      machineGroups[key].count++;
    });
    const uniqueMachines = Object.values(machineGroups);

    const qtyBadge = (count, bg = '#022040') => (
      <div style={{
        position: 'absolute', top: 8, right: 8,
        background: bg, color: '#fff', borderRadius: 36,
        minWidth: 72, padding: '10px 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 26, fontWeight: 800, zIndex: 10, whiteSpace: 'nowrap',
        boxShadow: '0 3px 10px rgba(0,0,0,0.35)', lineHeight: 1,
      }}>
        ×{count}
      </div>
    );

    return (
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0e7490', textTransform: 'uppercase', marginBottom: 10, letterSpacing: 0.5 }}>
          Vista de Instalación
        </div>

        {/* Single row: machines + consumables */}
        <div style={{ display: 'flex', alignItems: 'stretch' }}>
          <div style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0,
            background: 'linear-gradient(180deg, #f8fbff 0%, #edf3fa 100%)',
            border: '2px solid #d0d8e2', borderRadius: 8,
            padding: '16px 40px 0 40px', position: 'relative', minHeight: 620, flexWrap: 'wrap',
          }}>
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3,
              background: 'linear-gradient(90deg, #c4cdd8, #a0adb8, #c4cdd8)', borderRadius: '2px 2px 0 0' }} />

            {/* Machines */}
            {uniqueMachines.map(({ machine: m, count }, idx) => {
              const tag = (m.code || m.name || '').replace(/^(POS_)?(INT_|EXT_)?/, '').split(/[\s-]/)[0];
              const imgH   = Math.round(getModuleImageHeight(m) * SCALE);
              const imgOff = Math.round(getModuleBottomOffset(m) * SCALE);
              return (
                <div key={idx} style={{
                  display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
                  flexShrink: 0, position: 'relative', zIndex: 1, margin: 0, padding: 0, lineHeight: 0,
                }}>
                  {qtyBadge(count)}
                  <div style={{
                    width: IMG_W, height: imgH,
                    backgroundImage: `url(${getModuleImage(m)})`,
                    backgroundSize: 'contain', backgroundRepeat: 'no-repeat', backgroundPosition: 'center bottom',
                    flexShrink: 0, marginBottom: imgOff,
                  }} />
                  <span style={{ fontSize: 10, fontWeight: 700, color: '#022040', textAlign: 'center',
                    whiteSpace: 'nowrap', lineHeight: 'normal', margin: 0, padding: '4px 0 8px' }}>
                    {tag}
                  </span>
                </div>
              );
            })}

            {/* Consumables in same row */}
            {hasConsumables && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 24,
                marginLeft: uniqueMachines.length > 0 ? 40 : 0, marginBottom: 8, flexWrap: 'wrap',
                paddingBottom: 4,
              }}>
                {consumableItems.map((c, idx) => (
                  <div key={idx} style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    position: 'relative', flexShrink: 0,
                  }}>
                    {qtyBadge(c.qty, '#b45309')}
                    <div style={{ width: 420, height: 420, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img src={getModuleImage(c)} alt={c.name} style={{ maxWidth: 420, maxHeight: 420, objectFit: 'contain' }} />
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#022040', textAlign: 'center',
                      lineHeight: 1.3, margin: 0, padding: '8px 0 2px', maxWidth: 420 }}>
                      {c.name}
                    </span>
                    <span style={{ fontSize: 9, color: '#64748b', textAlign: 'center' }}>
                      {c.code}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  renderHero() {
    const { savedProposalId, proposalRef, clientName, savingProposal, saveSuccess, showPreview, generatingPdf, generatingPdfEn, cabinetItems, posItems, consumableItems } = this.state;
    const hasModules = cabinetItems.length > 0 || posItems.length > 0 || consumableItems.length > 0;
    const isEditing = !!savedProposalId;

    const badge = isEditing
      ? <span className="pconf-mode-badge editing">Editando propuesta</span>
      : <span className="pconf-mode-badge new">Nueva propuesta</span>;

    const subtitle = isEditing
      ? `${proposalRef ? `Ref: ${proposalRef}` : 'Sin referencia'}${clientName ? ` · ${clientName}` : ''}`
      : 'Configura y genera propuestas comerciales para máquinas Waterproof';

    return (
      <div className={`pconf-header${isEditing ? ' editing' : ''}`}>
        <div className="pconf-header-content">
          <div className="pconf-header-text">
            <h1>
              <FileText size={28} className="pconf-header-icon" />
              Configurador de Propuestas
              {badge}
            </h1>
            <span className="pconf-subtitle">
              {subtitle}
            </span>
          </div>
          <div className="pconf-header-actions">
            <button onClick={() => this.setState(p => ({ showPreview: !p.showPreview }))}
              className="pconf-header-btn outline"
              disabled={!hasModules}>
              {showPreview ? <EyeOff size={14} /> : <Eye size={14} />}
              {showPreview ? 'Editor' : 'Vista previa'}
            </button>
            <button onClick={this.resetForm}
              className="pconf-header-btn outline">
              <RotateCcw size={14} /> Limpiar
            </button>
            <button
              onClick={this.saveProposal}
              disabled={savingProposal || !hasModules}
              className={`pconf-header-btn ${isEditing ? 'save-update' : 'outline'}`}
            >
              {saveSuccess
                ? <><CheckCircle size={14} /> Guardada</>
                : <><Save size={14} /> {savingProposal ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Guardar')}</>}
            </button>
            <button
              onClick={() => this.props.history && this.props.history.push('/proposals')}
              className="pconf-header-btn outline"
            >
              <List size={14} /> Propuestas
            </button>
            <button onClick={this.savePdf} disabled={generatingPdf || generatingPdfEn || !hasModules}
              className="pconf-header-btn primary">
              <Download size={14} /> {generatingPdf ? 'Generando...' : 'Exportar PDF'}
            </button>
            <button onClick={this.savePdfEn} disabled={generatingPdf || generatingPdfEn || !hasModules}
              className="pconf-header-btn outline">
              <Download size={14} /> {generatingPdfEn ? 'Generating...' : 'Export PDF (EN)'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  renderSummaryBanner(t) {
    const { licenseType, maintenanceType, customization, installationType,
            lockType, saleMode, operatingMode, onlineSale, contractDuration, depositMonths,
            commissionPcts, hideMinAnnualCommission, installationCost,
            discountProduct, discountLicense, discountMaintenance,
            cabinetItems, posItems, consumableItems } = this.state;
    const modeLbl = SALE_MODES.find(m => m.value === saleMode)?.label || '';

    const kv = (label, value, bold) => (
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${BORDER}` }}>
        <span style={{ fontSize: 13, color: TEXT_2 }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: bold ? 700 : 600, color: TEXT_1 }}>{value}</span>
      </div>
    );

    const kvDiscount = (label, baseValue, discountedValue, discountPct, bold, suffix = '') => {
      if (!discountPct || discountPct <= 0) return kv(label, fmtAlways(discountedValue) + suffix, bold);
      const saving = discountedValue - baseValue;
      return (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${BORDER}` }}>
            <span style={{ fontSize: 13, color: TEXT_2 }}>{label}</span>
            <span style={{ fontSize: 13, color: TEXT_1 }}>{fmtAlways(baseValue)}{suffix}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: 'none' }}>
            <span style={{ fontSize: 12, color: AMBER, fontWeight: 600 }}>Dto. {discountPct}%</span>
            <span style={{ fontSize: 12, color: AMBER, fontWeight: 600 }}>{fmtAlways(saving)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0 5px', borderBottom: `1px solid ${BORDER}` }}>
            <span style={{ fontSize: 12, color: TEXT_2 }}>Total</span>
            <span style={{ fontSize: 13, fontWeight: bold ? 700 : 600, color: GREEN }}>{fmtAlways(discountedValue)}{suffix}</span>
          </div>
        </>
      );
    };

    const sectionHdr = (label, color) => (
      <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: color || TEAL, margin: '16px 0 8px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: color || TEAL, display: 'inline-block' }} />
        {label}
      </div>
    );

    const itemLine = (item, idx) => (
      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: `1px solid ${BORDER}`, fontSize: 12 }}>
        <span style={{ color: TEXT_2 }}>{item.name} <span style={{ color: TEXT_3 }}>×{item.qty}</span></span>
      </div>
    );

    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Left – Config overview */}
        <div style={{ ...cardStyle, background: '#fafbfc' }}>
          <div style={sectionTitle}>Configuración Oferta</div>
          {kv('Unidades', t.totalUnits)}
          {kv('Venta Online', onlineSale ? 'Sí' : 'No')}
          {kv('Tipo de Contrato', modeLbl, true)}
          {kv('Licencia', licenseType)}
          {kv('Personalización', customization ? 'Sí' : 'No')}
          {kv('Mantenimiento', MAINTENANCE_TYPES.find(m => m.value === maintenanceType)?.label || maintenanceType)}
        </div>

        {/* Center – Selected modules */}
        <div style={{ ...cardStyle, background: '#fafbfc' }}>
          <div style={sectionTitle}>Productos Seleccionados</div>
          {cabinetItems.length > 0 && (
            <>
              {sectionHdr('Máquinas')}
              {cabinetItems.map((it, i) => itemLine(it, i))}
            </>
          )}
          {consumableItems && consumableItems.length > 0 && (
            <>
              {sectionHdr('Consumibles', AMBER)}
              {consumableItems.map((it, i) => itemLine(it, i))}
            </>
          )}
          {cabinetItems.length === 0 && (!consumableItems || consumableItems.length === 0) && (
            <div style={{ padding: '20px 0', textAlign: 'center', color: TEXT_3, fontSize: 13, fontStyle: 'italic' }}>
              Sin productos añadidos
            </div>
          )}
        </div>

        {/* Right – CAPEX / OPEX */}
        <div style={cardStyle}>
          {saleMode === 'purchase' && (
            <>
              {sectionHdr('CAPEX')}
              {t.hasMachines && kvDiscount('Máquinas', t.baseSale, t.sale, discountProduct, true)}
              {t.consumableSale > 0 && kv('Consumibles', fmtAlways(t.consumableSale))}
              {t.hasMachines && kv('Personalización', customization ? fmtAlways(t.personalization) : 'No incluida')}
              {kv('Instalación, formación y puesta en marcha', installationCost > 0 ? fmtAlways(installationCost) : 'Pendiente')}
              {(() => { const totalCapex = t.sale + t.consumableSale + (customization && t.hasMachines ? t.personalization : 0) + (installationCost > 0 ? installationCost : 0); return kv('TOTAL CAPEX', fmtAlways(totalCapex), true); })()}

              {(licenseType !== 'NONE' || maintenanceType !== 'NONE') && t.hasMachines && sectionHdr('OPEX', GREEN)}
              {licenseType !== 'NONE' && t.hasMachines && kvDiscount('Licencias (' + licenseType + ')', t.baseLicenseMonth * 12, t.licenseAnnual, discountLicense, false, ' /año')}
              {maintenanceType !== 'NONE' && t.hasMachines && kvDiscount('Mantenimiento', t.baseMaintYear, t.maintenanceAnnual, discountMaintenance, false, ' /año')}
              {(licenseType !== 'NONE' || maintenanceType !== 'NONE') && t.hasMachines && kv('TOTAL OPEX', fmtAlways((licenseType !== 'NONE' ? t.licenseAnnual : 0) + (maintenanceType !== 'NONE' ? t.maintenanceAnnual : 0)) + ' /año', true)}
            </>
          )}

          {saleMode === 'rental' && (
            <>
              {sectionHdr('ALQUILER')}
              {t.rentalDiscountPct > 0 && (() => (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 10px', fontSize: 12, color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
                    <span>Precio base</span><span>{fmtAlways(t.baseRentAnnual) + ' /año'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 10px', fontSize: 12, color: '#059669', fontWeight: 600, borderBottom: '1px solid #f1f5f9' }}>
                    <span>Descuento global</span><span>-{t.rentalDiscountPct}%</span>
                  </div>
                </>
              ))()}
              {kv('Alquiler anual', fmtAlways(t.rentAnnual) + ' /año', true)}
              {kv('Alquiler mensual', fmtAlways(t.rentMonthly) + ' /mes', true)}
              {kv('Depósito (' + depositMonths + ' meses)', fmtAlways(t.deposit))}
              {kv('Duración contrato', Math.round(contractDuration / 12) + ' años')}
              {kv('Personalización', customization ? fmtAlways(t.personalization) : 'No incluida')}
              {kv('Instalación, formación y puesta en marcha', installationCost > 0 ? fmtAlways(installationCost) : 'Pendiente')}
              {t.consumableSale > 0 && kv('Consumibles (CAPEX)', fmtAlways(t.consumableSale))}

              {licenseType !== 'NONE' && sectionHdr('OPEX', GREEN)}
              {licenseType !== 'NONE' && kv('Licencias (' + licenseType + ')', 'Incluidas')}
            </>
          )}

          {saleMode === 'revenue_share' && (
            <>
              {sectionHdr('REVENUE SHARE')}
              {kv('Duración contrato', Math.round(contractDuration / 12) + ' años')}
              {(commissionPcts.length > 0 ? commissionPcts : [0]).map((pct, i) => kv(`Comisión Cliente Año ${i + 1}`, fmtPct(pct)))}
              {!hideMinAnnualCommission && kv('Comisión mínima anual (equivalente)', fmtAlways(t.rentAnnual) + ' /año', true)}
              {kv('Personalización', customization ? fmtAlways(t.personalization) : '-')}
              {kv('Instalación, formación y puesta en marcha', installationCost > 0 ? fmtAlways(installationCost) : 'Pendiente')}
              {t.consumableSale > 0 && kv('Consumibles (CAPEX)', fmtAlways(t.consumableSale))}

              {licenseType !== 'NONE' && sectionHdr('OPEX', GREEN)}
              {licenseType !== 'NONE' && kv('Licencias', 'Incluidas')}
            </>
          )}
        </div>
      </div>
    );
  }

  renderInputSection() {
    const { clientName, clientCompany, clientCIF, clientAddress,
            proposalRef, proposalDate, notes,
            licenseType, zone, customization,
            saleMode, onlineSale, contractDuration, depositMonths, maintenanceType,
            commissionPcts, hideMinAnnualCommission, installationCost, conditions,
            discountProduct, discountLicense, discountMaintenance,
            consumableItems, selectedConsumableCode } = this.state;

    const field = (lbl, value, onChange, type = 'text', extra = {}) => (
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>{lbl}</label>
        <input type={type} value={value} onChange={onChange} style={{ ...inputStyle, ...extra }} />
      </div>
    );

    const sel = (lbl, value, onChange, options) => (
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>{lbl}</label>
        <select value={value} onChange={onChange} style={selectStyle}>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
    );

    const toggle2 = (lbl, value, onChange, options, disabledValues = []) => (
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>{lbl}</label>
        <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: `1px solid ${BORDER}` }}>
          {options.map((o, idx) => {
            const active = o.value === value;
            const disabled = disabledValues.includes(o.value);
            return (
              <button key={o.value} type="button"
                disabled={disabled}
                onClick={() => !disabled && onChange({ target: { value: o.value } })}
                style={{
                  flex: 1, padding: '9px 12px', fontSize: 13, fontWeight: 600,
                  borderLeft: idx > 0 ? `1px solid ${BORDER}` : 'none',
                  borderTop: 'none', borderRight: 'none', borderBottom: 'none',
                  cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all 0.15s',
                  background: active ? 'linear-gradient(135deg, #022040 0%, #0a4080 100%)' : disabled ? '#f1f5f9' : '#fff',
                  color: active ? '#fff' : disabled ? TEXT_3 : TEXT_2,
                  opacity: disabled ? 0.5 : 1,
                }}>
                {o.label}
              </button>
            );
          })}
        </div>
      </div>
    );

    const pctField = (lbl, value, onChange) => (
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>{lbl}</label>
        <div style={{ position: 'relative' }}>
          <input type="number" step="0.5" value={value} onChange={onChange}
                 style={{ ...inputStyle, paddingRight: 32 }} />
          <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: TEXT_3 }}>%</span>
        </div>
      </div>
    );

    const numField = (lbl, value, onChange, suffix = '') => (
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>{lbl}</label>
        <div style={{ position: 'relative' }}>
          <input type="number" min="0" value={value} onChange={onChange}
                 style={{ ...inputStyle, paddingRight: suffix ? 40 : 12 }} />
          {suffix && <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: TEXT_3 }}>{suffix}</span>}
        </div>
      </div>
    );

    return (
      <><div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, marginBottom: 20 }}>
        {/* Client Info */}
        <div style={cardStyle}>
          <div style={sectionTitle}>Datos del Cliente</div>
          {field('Empresa / Razón social', clientCompany, this.set('clientCompany'))}
          {field('CIF / NIF', clientCIF, this.set('clientCIF'))}
          {field('Dirección', clientAddress, this.set('clientAddress'))}
          {field('Persona de contacto', clientName, this.set('clientName'))}
          {field('Referencia propuesta', proposalRef, this.set('proposalRef'))}
          {field('Fecha', proposalDate, this.set('proposalDate'), 'date')}
          {sel('Zona de Instalación', zone, this.set('zone'), ZONES)}
        </div>

        {/* Configuration */}
        <div style={cardStyle}>
          <div style={sectionTitle}>Configuración</div>
          {toggle2('Tipo de Licencia', licenseType, this.set('licenseType'), LICENSE_TYPES, saleMode === 'revenue_share' ? ['NONE'] : [])}
          {toggle2('Tipo de Mantenimiento', maintenanceType, this.set('maintenanceType'), MAINTENANCE_TYPES)}
          {toggle2('Venta Online', onlineSale, (e) => {
            const val = e.target.value === true;
            const update = { onlineSale: val };
            if (val && this.state.licenseType !== 'PRO') update.licenseType = 'PRO';
            this.setState(update);
          }, [{ value: false, label: 'No' }, { value: true, label: 'Sí' }])}
          {toggle2('Personalización', customization, (e) => this.setState({ customization: e.target.value === true }), [{ value: false, label: 'No' }, { value: true, label: 'Sí' }])}
          <div style={{ marginTop: 8 }}>
            <label style={labelStyle}>Observaciones</label>
            <textarea value={notes} onChange={this.set('notes')} rows={2}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
          </div>
        </div>

        {/* Sale Mode + Conditions */}
        <div style={cardStyle}>
          <div style={sectionTitle}>Tipo de Contrato</div>
          {toggle2('Contrato', saleMode, this.handleSaleModeChange, SALE_MODES)}

          {(saleMode === 'rental' || saleMode === 'revenue_share') && (
            numField('Duración contrato', Math.round(contractDuration / 12), (e) => {
              const years = Math.max(1, parseInt(e.target.value) || 1);
              const update = { contractDuration: years * 12 };
              if (this.state.saleMode === 'revenue_share') {
                const cur = this.state.commissionPcts;
                update.commissionPcts = Array.from({ length: years }, (_, i) => cur[i] !== undefined ? cur[i] : 0);
              }
              this.setState(update);
            }, 'años')
          )}
          {saleMode === 'rental' && (
            numField('Meses de depósito', depositMonths, (e) => this.setState({ depositMonths: Math.max(0, parseInt(e.target.value) || 0) }), 'meses')
          )}
          {saleMode === 'revenue_share' && (() => {
            const tCalc = this.calculateTotals();
            const years = Math.round(contractDuration / 12);
            const pcts = Array.from({ length: years }, (_, i) => commissionPcts[i] !== undefined ? commissionPcts[i] : 0);
            return (
              <>
                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>Comisión por año</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: 8, marginTop: 4 }}>
                    {pcts.map((pct, i) => (
                      <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                        <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Año {i + 1}</span>
                        <div style={{ display: 'flex', alignItems: 'center', border: `1px solid #e2e8f0`, borderRadius: 6, overflow: 'hidden', width: '100%' }}>
                          <input
                            type="number" min="0" max="100" step="0.5"
                            value={pct}
                            onChange={(e) => {
                              const v = parseFloat(e.target.value);
                              const next = [...pcts];
                              next[i] = isNaN(v) ? 0 : Math.max(0, Math.min(100, v));
                              this.setState({ commissionPcts: next });
                            }}
                            style={{ flex: 1, border: 'none', padding: '6px 4px', fontSize: 12, textAlign: 'center', background: '#fafafa', outline: 'none', width: 0 }}
                          />
                          <span style={{ padding: '0 6px', fontSize: 12, color: '#64748b', background: '#f1f5f9', borderLeft: '1px solid #e2e8f0', lineHeight: '30px', flexShrink: 0 }}>%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{ marginBottom: 14, padding: '8px 12px', background: '#f0f9ff', borderRadius: 6, border: '1px solid #bae6fd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: '#0369a1', fontWeight: 600 }}>Comisión mínima anual (calculada)</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#0369a1' }}>{fmtAlways(tCalc.rentAnnual)} /año</span>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, fontSize: 12, color: '#475569', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!hideMinAnnualCommission}
                    onChange={(e) => this.setState({ hideMinAnnualCommission: e.target.checked })}
                  />
                  Ocultar comisión mínima anual en resumen, vista previa y PDF
                </label>
              </>
            );
          })()}

          {numField('Instalación y puesta en marcha', installationCost, (e) => this.setState({ installationCost: parseFloat(e.target.value) || 0 }), '€')}

          <div style={sectionTitle}>Descuentos</div>
          {pctField('Descuento Producto', discountProduct, this.setDiscount('discountProduct'))}
          {saleMode !== 'revenue_share' && licenseType !== 'NONE' && pctField('Descuento Licencias', discountLicense, this.setDiscount('discountLicense'))}
          {maintenanceType !== 'NONE' && pctField('Descuento Mantenimiento', discountMaintenance, this.setDiscount('discountMaintenance'))}

          <div style={{ marginTop: 8 }}>
            <label style={labelStyle}>Condiciones</label>
            <textarea value={conditions} onChange={this.set('conditions')} rows={3}
              placeholder="Precio ex-works Cataluña. Transporte no incluido..."
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
          </div>
        </div>
      </div>

        {/* Consumibles */}
        {(() => {
          const thS = { padding: '10px 8px', textAlign: 'right', fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: TH_COLOR, background: TH_BG, borderBottom: `2px solid ${BORDER}`, whiteSpace: 'nowrap' };
          const tdS = { padding: '8px 8px', textAlign: 'right', borderBottom: `1px solid #f1f5f9`, fontSize: 13, color: TEXT_1 };
          const hidePrice = this.state.saleMode === 'revenue_share';
          return (
            <div style={{ ...cardStyle, marginTop: 20, marginBottom: 20, padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '16px 24px 12px', borderBottom: `1px solid ${BORDER}` }}>
                <div style={sectionTitle}>Consumibles</div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <select value={selectedConsumableCode}
                    onChange={(e) => this.setState({ selectedConsumableCode: e.target.value })}
                    style={{ ...selectStyle, width: 400, maxWidth: '100%' }}>
                    <option value="">— Seleccionar consumible —</option>
                    {this.getProducts('consumible')
                      .filter(p => !consumableItems.some(it => it.code === p.code))
                      .map(p => (
                        <option key={p.code} value={p.code}>{p.name}</option>
                      ))}
                  </select>
                  <button onClick={this.addConsumable} disabled={!selectedConsumableCode}
                    style={{ background: selectedConsumableCode ? 'linear-gradient(135deg, #022040 0%, #0a4080 100%)' : BORDER, border: 'none', color: '#fff', padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: selectedConsumableCode ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Plus size={14} /> Añadir
                  </button>
                </div>
              </div>
              {consumableItems.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 500 }}>
                    <thead>
                      <tr>
                        <th style={{ ...thS, textAlign: 'left', minWidth: 200 }}>Consumible</th>
                        {!hidePrice && <th style={{ ...thS, minWidth: 100 }}>Precio ud.</th>}
                        <th style={{ ...thS, minWidth: 70 }}>Cantidad</th>
                        {!hidePrice && <th style={{ ...thS, minWidth: 100 }}>Total Venta</th>}
                        <th style={{ ...thS, minWidth: 50 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {consumableItems.map((it, idx) => (
                        <tr key={idx} style={{ background: ROW_HL }}>
                          <td style={{ ...tdS, textAlign: 'left', fontWeight: 600 }}>{it.name} <span style={{ fontSize: 10, color: TEXT_2, fontWeight: 400 }}>({it.code})</span></td>
                          {!hidePrice && <td style={tdS}>{fmtDec(this.getConsumableUnitPrice(it))}</td>}
                          <td style={{ ...tdS, padding: '4px 4px' }}>
                            <input type="number" min="1" value={it.qty} onChange={this.setConsumableQty(idx)}
                              style={{ width: 52, padding: '6px 4px', borderRadius: 6, border: `1px solid #0a4080`, textAlign: 'center', fontSize: 13, outline: 'none', background: '#fff' }} />
                          </td>
                          {!hidePrice && <td style={{ ...tdS, fontWeight: 600, color: TEAL }}>{fmtDec(it.qty * this.getConsumableUnitPrice(it))}</td>}
                          <td style={tdS}>
                            <button onClick={() => this.removeConsumable(idx)}
                              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: RED, padding: 4 }}>
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                      <tr style={{ background: '#f8fafc' }}>
                        <td style={{ ...tdS, textAlign: 'left', fontWeight: 700, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}>Subtotal</td>
                        {!hidePrice && <td style={{ ...tdS, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}></td>}
                        <td style={{ ...tdS, fontWeight: 700, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}>{consumableItems.reduce((s, it) => s + it.qty, 0)}</td>
                        {!hidePrice && <td style={{ ...tdS, fontWeight: 700, borderTop: `2px solid ${BORDER}`, borderBottom: 'none', color: TEAL }}>{fmtDec(consumableItems.reduce((s, it) => s + it.qty * this.getConsumableUnitPrice(it), 0))}</td>}
                        <td style={{ ...tdS, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
              {consumableItems.length === 0 && (
                <div style={{ padding: '32px 24px', textAlign: 'center', color: TEXT_3, fontSize: 14 }}>
                  Selecciona un consumible y pulsa "Añadir" para agregarlo a la propuesta
                </div>
              )}
            </div>
          );
        })()}
      </>
    );
  }

  renderProductSection(title, category, items, availableProducts) {
    const selKey = category === 'cabinet' ? 'selectedCabinetCode' : 'selectedPosCode';
    const selectedCode = this.state[selKey];
    const { saleMode } = this.state;
    const showSale = saleMode === 'purchase';
    const showRent = saleMode === 'rental' || saleMode === 'revenue_share';
    const priceLabel = showSale ? 'Precio' : 'Alq./mes';

    const thS = { padding: '10px 8px', textAlign: 'right', fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: TH_COLOR, background: TH_BG, borderBottom: `2px solid ${BORDER}`, whiteSpace: 'nowrap' };
    const tdS = { padding: '8px 8px', textAlign: 'right', borderBottom: `1px solid #f1f5f9`, fontSize: 13, color: TEXT_1 };

    const sub = items.reduce((a, it) => ({
      units: a.units + it.qty,
      lockers: a.lockers + it.qty * (it.lockers || 0),
      sale: a.sale + it.qty * it.unitPrice,
      rent: a.rent + it.qty * it.unitRent,
    }), { units: 0, lockers: 0, sale: 0, rent: 0 });

    return (
      <div style={{ ...cardStyle, marginBottom: 20, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '16px 24px 12px', borderBottom: `1px solid ${BORDER}` }}>
          <div style={sectionTitle}>{title}</div>
          {(() => {
            const filteredProducts = availableProducts.filter(p => !items.some(it => it.code === p.code));
            return (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <select value={selectedCode} onChange={(e) => this.setState({ [selKey]: e.target.value })}
                  style={{ ...selectStyle, width: 400, maxWidth: '100%' }}>
                  <option value="">— Seleccionar producto —</option>
                  {filteredProducts.map(p => (
                    <option key={p.code} value={p.code}>
                      {p.name}
                    </option>
                  ))}
                </select>
                <button onClick={() => this.addItem(category)} disabled={!selectedCode}
                  style={{ background: selectedCode ? 'linear-gradient(135deg, #022040 0%, #0a4080 100%)' : BORDER, border: 'none', color: '#fff', padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: selectedCode ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Plus size={14} /> Añadir
                </button>
              </div>
            );
          })()}
        </div>

        {items.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
              <thead>
                <tr>
                  <th style={{ ...thS, textAlign: 'left', minWidth: 200 }}>Producto</th>
                  <th style={{ ...thS, minWidth: 80 }}>{priceLabel}</th>
                  <th style={{ ...thS, minWidth: 60 }}>Cantidad</th>
                  <th style={{ ...thS, minWidth: 85 }}>{showSale ? 'Total Venta' : 'Total Alquiler'}</th>
                  <th style={{ ...thS, minWidth: 50 }}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={idx} style={{ background: ROW_HL }}>
                    <td style={{ ...tdS, textAlign: 'left', fontWeight: 600 }}>{it.name} <span style={{ fontSize: 10, color: TEXT_2, fontWeight: 400 }}>({it.code})</span></td>
                    <td style={tdS}>{fmtAlways(showSale ? it.unitPrice : it.unitRent)}</td>
                    <td style={{ ...tdS, padding: '4px 4px' }}>
                      <input type="number" min="1" value={it.qty} onChange={this.setItemQty(category, idx)}
                        style={{ width: 52, padding: '6px 4px', borderRadius: 6, border: `1px solid #0a4080`, textAlign: 'center', fontSize: 13, outline: 'none', background: '#fff' }} />
                    </td>
                    <td style={{ ...tdS, fontWeight: 600, color: showSale ? TEAL : GREEN }}>{fmtAlways(it.qty * (showSale ? it.unitPrice : it.unitRent))}</td>
                    <td style={tdS}>
                      <button onClick={() => this.removeItem(category, idx)}
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: RED, padding: 4 }}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
                <tr style={{ background: '#f8fafc' }}>
                  <td style={{ ...tdS, textAlign: 'left', fontWeight: 700, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}>Subtotal</td>
                  <td style={{ ...tdS, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}></td>
                  <td style={{ ...tdS, fontWeight: 700, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}>{sub.units}</td>
                  <td style={{ ...tdS, fontWeight: 700, borderTop: `2px solid ${BORDER}`, borderBottom: 'none', color: showSale ? TEAL : GREEN }}>{fmtAlways(showSale ? sub.sale : sub.rent)}</td>
                  <td style={{ ...tdS, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {items.length === 0 && (
          <div style={{ padding: '32px 24px', textAlign: 'center', color: TEXT_3, fontSize: 14 }}>
            Selecciona un producto y pulsa "Añadir" para agregar elementos a la propuesta
          </div>
        )}
      </div>
    );
  }

  renderGrandTotal(t) {
    const { saleMode, licenseType, commissionPcts } = this.state;
    const c = { padding: '12px 16px', textAlign: 'center', fontWeight: 700, fontSize: 15, color: '#fff' };
    const lbl = { fontSize: 11, color: '#a8c8e8', marginBottom: 4, fontWeight: 400 };
    const avgCommission = commissionPcts.length > 0
      ? Math.round(commissionPcts.reduce((a, b) => a + b, 0) / commissionPcts.length * 10) / 10
      : 0;

    return (
      <div style={{ marginBottom: 20, background: `linear-gradient(135deg, #022040 0%, #0a4080 100%)`, borderRadius: 12, overflow: 'hidden', border: `1px solid #0a4080` }}>
        <div style={{ padding: '14px 24px', borderBottom: `1px solid rgba(255,255,255,0.15)` }}>
          <span style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#fff' }}>Resumen Económico</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: saleMode === 'purchase' ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)', gap: 0 }}>
          <div style={c}>
            <div style={lbl}>Unidades</div>
            {t.totalUnits}
          </div>
          {saleMode === 'purchase' && (
            <div style={c}>
              <div style={lbl}>CAPEX (Venta)</div>
              {fmtAlways(t.sale)}
            </div>
          )}
          <div style={c}>
            <div style={{ ...lbl, color: GREEN }}>
              {saleMode === 'purchase' ? (licenseType === 'NONE' ? 'Licencia' : 'OPEX Licencias/año') : saleMode === 'rental' ? 'Alquiler/mes' : 'Comisión media'}
            </div>
            <span style={{ color: GREEN }}>
              {saleMode === 'purchase'
                ? (licenseType === 'NONE' ? 'Sin Licencia' : fmtAlways(t.licenseAnnual))
                : saleMode === 'rental' ? fmtAlways(t.rentMonthly) : fmtPct(avgCommission)}
            </span>
          </div>
          <div style={c}>
            <div style={{ ...lbl, color: AMBER }}>Personalización</div>
            <span style={{ color: AMBER }}>{fmtAlways(t.personalization)}</span>
          </div>
        </div>
      </div>
    );
  }

  renderPrintCoverPage() {
    const { proposalRef, proposalDate, clientName, clientCompany, exportLang } = this.state;
    const lang = exportLang || 'es';
    const preparedBy = this.props.fullname || '';

    return (
      <div style={{
        width: '100%', height: 1018, display: 'flex', flexDirection: 'column',
        background: '#fff', fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
        position: 'relative', overflow: 'hidden',
      }}>
        <img
          src="/images/configurator/portada.png"
          alt=""
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
        />
        <div style={{ padding: '32px 48px 0', display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', zIndex: 1 }}>
          <img src="/images/logo_white.png" alt="VACWAYone" style={{ height: 36, width: 'auto' }} />
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', paddingTop: 60, position: 'relative', zIndex: 1 }}>
          <div style={{ fontSize: 56, fontWeight: 700, lineHeight: 1, letterSpacing: 2, textTransform: 'uppercase', textAlign: 'center' }}>
            <span style={{ color: '#f59e0b' }}>{lang === 'en' ? 'COMMERCIAL ' : 'OFERTA '}</span>
            <span style={{ color: '#ffffff' }}>{lang === 'en' ? 'OFFER' : 'COMERCIAL'}</span>
          </div>
          <div style={{ fontSize: 22, lineHeight: 1, letterSpacing: 4, textTransform: 'uppercase', textAlign: 'center', marginTop: 14 }}>
            <span style={{ color: '#ffffff', fontWeight: 300 }}>VACWAY</span>
            <span style={{ color: '#ffffff', fontWeight: 700 }}>waterproof</span>
          </div>
        </div>
        <div style={{ padding: '12px 48px', background: 'transparent', position: 'absolute', bottom: 30, left: 0, right: 0, zIndex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: 6 }}>{lang === 'en' ? 'Prepared by' : 'Realizado por'}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#ffffff' }}>{preparedBy || '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: 6 }}>{lang === 'en' ? 'Offer Reference' : 'Referencia de la oferta'}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#ffffff' }}>{proposalRef || '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: 6 }}>{lang === 'en' ? 'Date' : 'Fecha'}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#ffffff' }}>
                {proposalDate ? new Date(proposalDate + 'T00:00:00').toLocaleDateString(lang === 'en' ? 'en-GB' : 'es-ES', { year: 'numeric', month: 'long', day: 'numeric' }) : '—'}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderPrintHeader(s = 1) {
    const { clientName, clientCompany, proposalRef, proposalDate, exportLang } = this.state;
    const lang = exportLang || 'es';
    const px = (n) => Math.round(n * s);
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: px(20), borderBottom: `${Math.max(2, Math.round(3 * s))}px solid #0a4080`, paddingBottom: px(16) }}>
        <div>
          <img src="/images/logo_blue.png" alt="VACWAYone" style={{ height: px(32), width: 'auto', display: 'block' }} />
          <div style={{ fontSize: px(10), color: '#999', marginTop: px(2) }}>{lang === 'en' ? 'Vending machine' : 'Máquina vending'}</div>
        </div>
        <div style={{ textAlign: 'right', fontSize: px(11), color: '#666' }}>
          <div style={{ fontSize: px(16), fontWeight: 700, color: '#333', marginBottom: px(4) }}>{lang === 'en' ? 'COMMERCIAL PROPOSAL' : 'PROPUESTA COMERCIAL'}</div>
          {proposalRef && <div>Ref: {proposalRef}</div>}
          <div>{lang === 'en' ? 'Date: ' : 'Fecha: '}{proposalDate}</div>
          {clientCompany && <div>{lang === 'en' ? 'Company: ' : 'Empresa: '}{clientCompany}</div>}
          {clientName && <div>{lang === 'en' ? 'Contact: ' : 'Contacto: '}{clientName}</div>}
        </div>
      </div>
    );
  }

  renderPrintSignatureBlock(s = 1) {
    const { clientName, clientCompany, clientCIF, clientAddress, exportLang } = this.state;
    const lang = exportLang || 'es';
    const px = (n) => Math.round(n * s);
    return (
      <div style={{ marginTop: px(20), pageBreakBefore: 'auto' }}>
        <div style={{ fontSize: px(11), color: '#0e7490', fontStyle: 'italic', marginBottom: px(14) }}>
          {lang === 'en'
            ? 'To accept this proposal, please return this document signed and stamped with the following information:'
            : 'Para la aceptación del presupuesto, por favor devuelva este documento firmado y sellado con la siguiente información:'}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: px(30) }}>
          {/* Left: Company Details */}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: px(12), fontWeight: 700, color: '#0e7490', textTransform: 'uppercase', marginBottom: px(10), display: 'flex', alignItems: 'center', gap: px(6) }}>
              <span style={{ color: '#0e7490' }}>➜</span> {lang === 'en' ? 'Company Details' : 'Datos de la Empresa'}
            </div>
            {(lang === 'en'
              ? [['Company Name', clientCompany], ['Tax ID / VAT Number', clientCIF], ['Registered Address', clientAddress], ['Full Name', clientName]]
              : [['Razón social', clientCompany], ['CIF / NIF', clientCIF], ['Dirección del domicilio social', clientAddress], ['Nombre y apellidos', clientName]]
            ).map(([lbl, val], i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', marginBottom: px(8) }}>
                <span style={{ width: px(200), fontSize: px(11), color: '#555', flexShrink: 0 }}>{lbl}</span>
                <div style={{ flex: 1, borderBottom: '1px solid #ccc', minHeight: px(20), fontSize: px(11), paddingBottom: px(2) }}>{val}</div>
              </div>
            ))}
          </div>
          {/* Right: Firma y sello */}
          <div style={{ textAlign: 'center', flexShrink: 0 }}>
            <div style={{ fontSize: px(12), fontWeight: 700, color: '#0e7490', textTransform: 'uppercase', marginBottom: px(10), display: 'flex', alignItems: 'center', gap: px(6) }}>
              <span style={{ color: '#0e7490' }}>➜</span> {lang === 'en' ? 'Signature and Stamp' : 'Firma y Sello'}
            </div>
            <div style={{ width: px(220), height: px(110), border: '1px solid #ccc', borderRadius: px(6) }}></div>
            <div style={{ fontSize: px(10), color: '#888', marginTop: px(8) }}>{lang === 'en' ? 'Date: _______________' : 'Fecha: _______________'}</div>
          </div>
        </div>
      </div>
    );
  }

  renderPrintVisualPage() {
    const s = 2.75;
    return (
      <div style={{ color: '#333', lineHeight: 1.5 }}>
        {this.renderPrintHeader(s)}
        {this.renderPrintVisualLine()}
        {this.renderPrintSignatureBlock(s)}
      </div>
    );
  }

  renderPrintDataPage(t) {
    const { saleMode, cabinetItems, posItems, consumableItems,
            licenseType, customization, maintenanceType,
            onlineSale, zone, exportLang } = this.state;
    const lang = exportLang || 'es';

    const thP = { padding: '6px 8px', textAlign: 'right', fontWeight: 600, fontSize: 9, textTransform: 'uppercase', color: '#0e7490', background: '#e0f7fa', borderBottom: '2px solid #b2ebf2', whiteSpace: 'nowrap' };
    const tdP = { padding: '5px 8px', textAlign: 'right', borderBottom: '1px solid #e8e8e8', fontSize: 11, color: '#333' };
    const showSale = saleMode === 'purchase';

    const renderPrintTable = (title, items, alwaysCapex = false, unitFmt = fmtAlways) => {
      if (!items || items.length === 0) return null;
      const showSaleItem = showSale || alwaysCapex;
      const sub = items.reduce((a, it) => ({
        units: a.units + it.qty,
        sale: a.sale + it.qty * (it.unitPrice || 0),
        rent: a.rent + it.qty * (it.unitRent || 0),
      }), { units: 0, sale: 0, rent: 0 });

      return (
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#0e7490', textTransform: 'uppercase', marginBottom: 6, letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: '#0e7490' }}>➜</span> {title}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...thP, textAlign: 'left', minWidth: 90, width: 90 }}>{lang === 'en' ? 'Code' : 'Código'}</th>
                <th style={{ ...thP, textAlign: 'left', minWidth: 160 }}>{lang === 'en' ? 'Description' : 'Descripción'}</th>
                <th style={thP}>{lang === 'en' ? 'Quantity' : 'Cantidad'}</th>
                {showSaleItem && <th style={thP}>{lang === 'en' ? 'Unit Price' : 'Precio Ud.'}</th>}
                {showSaleItem && <th style={thP}>{lang === 'en' ? 'Total Sale' : 'Total Venta'}</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={idx} style={{ background: idx % 2 === 0 ? '#f0f9ff' : '#fff' }}>
                  <td style={{ ...tdP, textAlign: 'left', fontWeight: 600, fontSize: 9, color: '#64748b', whiteSpace: 'nowrap' }}>{it.code}</td>
                  <td style={{ ...tdP, textAlign: 'left', fontWeight: 600 }}>{it.name}</td>
                  <td style={{ ...tdP, fontWeight: 600 }}>{it.qty}</td>
                  {showSaleItem && <td style={tdP}>{unitFmt(it.unitPrice || 0)}</td>}
                  {showSaleItem && <td style={{ ...tdP, fontWeight: 600 }}>{unitFmt(it.qty * (it.unitPrice || 0))}</td>}
                </tr>
              ))}
              <tr style={{ background: '#f8f8f8' }}>
                <td style={{ ...tdP, textAlign: 'left', fontWeight: 700, borderTop: '2px solid #ccc', borderBottom: 'none' }} colSpan={2}>{lang === 'en' ? 'Subtotal' : 'Subtotal'}</td>
                <td style={{ ...tdP, fontWeight: 700, borderTop: '2px solid #ccc', borderBottom: 'none' }}>{sub.units}</td>
                {showSaleItem && <td style={{ ...tdP, borderTop: '2px solid #ccc', borderBottom: 'none' }}></td>}
                {showSaleItem && <td style={{ ...tdP, fontWeight: 700, borderTop: '2px solid #ccc', borderBottom: 'none', color: '#0e7490' }}>{unitFmt(sub.sale)}</td>}
              </tr>
            </tbody>
          </table>
        </div>
      );
    };

    return (
      <div style={{ color: '#333', lineHeight: 1.5 }}>
        {this.renderPrintHeader()}

        <div style={{ fontSize: 14, fontWeight: 700, color: '#0e7490', textTransform: 'uppercase', marginBottom: 12, letterSpacing: 0.5 }}>
          {lang === 'en' ? 'Installation Proposal' : 'Propuesta de Instalación'}
        </div>

        {(() => {
          const cfgItems = [
            { label: lang === 'en' ? 'Machines' : 'Máquinas',             value: t.totalCabinets },
            { label: lang === 'en' ? 'Online Sales' : 'Venta Online',     value: onlineSale ? (lang === 'en' ? 'Yes' : 'Sí') : 'No' },
            { label: lang === 'en' ? 'Contract Type' : 'Tipo de Contrato', value: lang === 'en' ? (SALE_MODE_EN[saleMode] || saleMode) : (SALE_MODES.find(m => m.value === saleMode)?.label || saleMode) },
            { label: lang === 'en' ? 'Zone' : 'Zona',                     value: zone },
            { label: lang === 'en' ? 'License' : 'Licencia',              value: lang === 'en' ? (LICENSE_EN[licenseType] || licenseType) : (licenseType === 'NONE' ? 'Sin licencia' : licenseType) },
            { label: lang === 'en' ? 'Customization' : 'Personalización', value: customization ? (lang === 'en' ? 'Yes' : 'Sí') : 'No' },
            { label: lang === 'en' ? 'Maintenance' : 'Mantenimiento',     value: lang === 'en' ? (MAINT_EN[maintenanceType] || maintenanceType) : (MAINTENANCE_TYPES.find(m => m.value === maintenanceType)?.label || maintenanceType) },
          ];
          const half = Math.ceil(cfgItems.length / 2);
          const col1 = cfgItems.slice(0, half);
          const col2 = cfgItems.slice(half);
          const cellLbl = { padding: '5px 8px', fontSize: 10, color: '#64748b', fontWeight: 600, borderBottom: '1px solid #f1f5f9', background: '#f8fafc', whiteSpace: 'nowrap' };
          const cellVal = { padding: '5px 10px', fontSize: 10, color: '#1e293b', fontWeight: 700, borderBottom: '1px solid #f1f5f9', borderLeft: '1px solid #e2e8f0' };
          const renderCol = (rows) => (
            <table style={{ flex: 1, borderCollapse: 'collapse', border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td style={cellLbl}>{r.label}</td>
                    <td style={cellVal}>{r.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          );
          return (
            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              {renderCol(col1)}
              {renderCol(col2)}
            </div>
          );
        })()}

        {renderPrintTable(lang === 'en' ? 'Machines' : 'Máquinas', cabinetItems)}
        {renderPrintTable(lang === 'en' ? 'Consumables' : 'Consumibles', (consumableItems || []).map(it => ({ ...it, unitPrice: this.getConsumableUnitPrice(it) })), saleMode !== 'revenue_share', fmtDec)}
      </div>
    );
  }

  renderPrintOfertaPage(t, includeSignature = false) {
    const { notes,
            licenseType, maintenanceType, customization,
            saleMode, contractDuration, depositMonths,
            commissionPcts, hideMinAnnualCommission, installationCost, conditions,
            discountProduct, discountMaintenance, discountLicense, exportLang,
            consumableItems } = this.state;

    const lang     = exportLang || 'es';
    const modeLbl  = lang === 'en' ? (SALE_MODE_EN[saleMode] || saleMode) : (SALE_MODES.find(m => m.value === saleMode)?.label || '');
    const maintLbl = lang === 'en' ? (MAINT_EN[maintenanceType] || maintenanceType) : (MAINTENANCE_TYPES.find(m => m.value === maintenanceType)?.label || '');
    const perYear  = lang === 'en' ? ' /year' : ' /año';
    const perMonth = lang === 'en' ? ' /month' : ' /mes';
    const notIncl  = lang === 'en' ? 'Not included' : 'No incluida';
    const pending  = lang === 'en' ? 'Pending' : 'Pendiente';
    const included = lang === 'en' ? 'Included' : 'Incluidas';
    const yrsVal   = (months) => lang === 'en' ? Math.round(months / 12) + ' year(s)' : Math.round(months / 12) + ' años';

    const printKv = (label, value, bold) => (
      <div style={{ display: 'flex', borderBottom: '1px solid #eee', background: bold ? '#f0f9ff' : undefined }}>
        <div style={{ width: 16, padding: '5px 0 5px 10px', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          <span style={{ color: '#0e7490', fontSize: 11 }}>➜</span>
        </div>
        <div style={{ flex: 1, padding: '5px 8px', fontSize: 11, color: bold ? '#0e7490' : '#555', fontWeight: bold ? 700 : 400 }}>{label}</div>
        <div style={{ width: 120, padding: '5px 10px', fontSize: 11, fontWeight: bold ? 700 : 600, color: bold ? '#0e7490' : '#333', borderLeft: '1px solid #eee', textAlign: 'right', flexShrink: 0 }}>{value}</div>
      </div>
    );

    const printKvDiscount = (label, baseValue, discountedValue, discountPct, suffix = '') => {
      if (!discountPct || discountPct <= 0) return printKv(label, fmtAlways(discountedValue) + suffix);
      const saving = discountedValue - baseValue;
      return (
        <>
          <div style={{ display: 'flex', borderBottom: '1px solid #eee' }}>
            <div style={{ width: 16, padding: '5px 0 5px 10px', flexShrink: 0, display: 'flex', alignItems: 'center' }}><span style={{ color: '#0e7490', fontSize: 11 }}>➜</span></div>
            <div style={{ flex: 1, padding: '5px 8px', fontSize: 11, color: '#555' }}>{label}</div>
            <div style={{ width: 120, padding: '5px 10px', fontSize: 11, fontWeight: 600, color: '#333', borderLeft: '1px solid #eee', textAlign: 'right', flexShrink: 0 }}>{fmtAlways(baseValue)}{suffix}</div>
          </div>
          <div style={{ display: 'flex', borderBottom: '1px solid #eee', background: '#fffbeb' }}>
            <div style={{ width: 16, flexShrink: 0 }} />
            <div style={{ flex: 1, padding: '3px 8px 5px', fontSize: 10, color: '#b45309' }}>{lang === 'en' ? 'Disc. ' : 'Dto. '}{discountPct}%</div>
            <div style={{ width: 120, padding: '3px 10px 5px', fontSize: 11, fontWeight: 600, color: '#b45309', borderLeft: '1px solid #eee', textAlign: 'right', flexShrink: 0 }}>{fmtAlways(saving)}</div>
          </div>
          <div style={{ display: 'flex', borderBottom: '1px solid #eee' }}>
            <div style={{ width: 16, flexShrink: 0 }} />
            <div style={{ flex: 1, padding: '3px 8px 5px', fontSize: 10, color: '#555' }}>Total</div>
            <div style={{ width: 120, padding: '3px 10px 5px', fontSize: 11, fontWeight: 700, color: '#0e7490', borderLeft: '1px solid #eee', textAlign: 'right', flexShrink: 0 }}>{fmtAlways(discountedValue)}{suffix}</div>
          </div>
        </>
      );
    };

    const printSectionHdr = (label) => (
      <div style={{ fontSize: 12, fontWeight: 700, color: '#333', margin: '14px 0 6px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#333', display: 'inline-block' }} />
        {label}
      </div>
    );

    return (
      <div style={{ color: '#333', lineHeight: 1.5 }}>
        {this.renderPrintHeader()}

        <div style={{ marginTop: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0e7490', textTransform: 'uppercase', marginBottom: 4, letterSpacing: 0.5 }}>
            {lang === 'en' ? 'Economic Offer and Conditions' : 'Oferta Económica y Condiciones'}
          </div>
          <div style={{ background: '#0e7490', color: '#fff', borderRadius: 6, padding: '8px 14px', fontSize: 12, fontWeight: 700, marginBottom: 10, display: 'inline-block' }}>
            {modeLbl}
          </div>

          {saleMode === 'purchase' && (
            <div style={{ overflow: 'hidden', marginBottom: 16 }}>
              {printSectionHdr('CAPEX')}
              {printKvDiscount(lang === 'en' ? 'Machines' : 'Máquinas', t.baseSale, t.sale, discountProduct)}
              {printKv(lang === 'en' ? 'Customization' : 'Personalización', customization ? fmtAlways(t.personalization) : notIncl)}
              {printKv(lang === 'en' ? 'Installation, training and commissioning' : 'Instalación, formación y puesta en marcha', installationCost > 0 ? fmtAlways(installationCost) : pending)}
              {t.consumableSale > 0 && printKv(lang === 'en' ? 'Consumables' : 'Consumibles', fmtAlways(t.consumableSale))}
              {printKv('TOTAL CAPEX', fmtAlways(t.sale + t.consumableSale + (customization ? t.personalization : 0) + (installationCost > 0 ? installationCost : 0)), true)}

              {(licenseType !== 'NONE' || maintenanceType !== 'NONE') && printSectionHdr('OPEX')}
              {licenseType !== 'NONE' && printKvDiscount((lang === 'en' ? 'Licenses (' : 'Licencias (') + licenseType + ')', t.baseLicenseMonth * 12, t.licenseAnnual, discountLicense, perYear)}
              {maintenanceType !== 'NONE' && printKvDiscount((lang === 'en' ? 'Maintenance (' : 'Mantenimiento (') + maintLbl + ')', t.baseMaintYear, t.maintenanceAnnual, discountMaintenance, perYear)}
              {(licenseType !== 'NONE' || maintenanceType !== 'NONE') && printKv('TOTAL OPEX', fmtAlways((licenseType !== 'NONE' ? t.licenseAnnual : 0) + (maintenanceType !== 'NONE' ? t.maintenanceAnnual : 0)) + perYear, true)}
            </div>
          )}

          {saleMode === 'rental' && (
            <div style={{ marginBottom: 16 }}>
              {printSectionHdr(lang === 'en' ? 'RENTAL' : 'ALQUILER')}
              {t.rentalDiscountPct > 0 && printKv(lang === 'en' ? 'Base price' : 'Precio base', fmtAlways(t.baseRentAnnual) + perYear)}
              {t.rentalDiscountPct > 0 && printKv(lang === 'en' ? 'Global discount applied' : 'Descuento global aplicado', '-' + t.rentalDiscountPct + '%')}
              {printKv(lang === 'en' ? 'Annual Rental' : 'Alquiler anual', fmtAlways(t.rentAnnual) + perYear, true)}
              {printKv(lang === 'en' ? 'Monthly Rental' : 'Alquiler mensual', fmtAlways(t.rentMonthly) + perMonth, true)}
              {printKv(lang === 'en' ? depositMonths + '-Month Deposit' : 'Depósito ' + depositMonths + ' meses', fmtAlways(t.deposit))}
              {printKv(lang === 'en' ? 'Contract Duration (mandatory commitment)' : 'Duración del contrato (compromiso obligatorio)', yrsVal(contractDuration))}
              {printKv(lang === 'en' ? 'Customization' : 'Personalización', customization ? fmtAlways(t.personalization) : notIncl)}
              {printKv(lang === 'en' ? 'Installation, training and commissioning' : 'Instalación, formación y puesta en marcha', installationCost > 0 ? fmtAlways(installationCost) : pending)}
              {t.consumableSale > 0 && printKv(lang === 'en' ? 'Consumables (CAPEX)' : 'Consumibles (CAPEX)', fmtAlways(t.consumableSale))}
              {licenseType !== 'NONE' && printSectionHdr('OPEX')}
              {licenseType !== 'NONE' && printKv(lang === 'en' ? 'Licenses (' + licenseType + ')' : 'Licencias (' + licenseType + ')', included)}
            </div>
          )}

          {saleMode === 'revenue_share' && (
            <div style={{ marginBottom: 16 }}>
              {printSectionHdr('REVENUE SHARE')}
              {printKv(lang === 'en' ? 'Contract Duration' : 'Duración del contrato', yrsVal(contractDuration))}
              {commissionPcts.map((pct, i) => printKv(lang === 'en' ? `Client Commission Year ${i + 1}` : `Comisión Cliente Año ${i + 1}`, fmtPct(pct)))}
              {!hideMinAnnualCommission && printKv(lang === 'en' ? 'Minimum Annual Commission Clause' : 'Cláusula de comisiones anuales mínimas', fmtAlways(t.rentAnnual) + perYear, true)}
              {printKv(lang === 'en' ? 'Customization' : 'Personalización', customization ? fmtAlways(t.personalization) : notIncl)}
              {printKv(lang === 'en' ? 'Installation, training and commissioning' : 'Instalación, formación y puesta en marcha', installationCost > 0 ? fmtAlways(installationCost) : pending)}
              {t.consumableSale > 0 && printKv(lang === 'en' ? 'Consumables (CAPEX)' : 'Consumibles (CAPEX)', fmtAlways(t.consumableSale))}
              {(licenseType !== 'NONE' || maintenanceType !== 'NONE') && printSectionHdr('OPEX')}
              {licenseType !== 'NONE' && printKv(lang === 'en' ? 'Licenses' : 'Licencias', included)}
              {maintenanceType !== 'NONE' && printKv(lang === 'en' ? 'Maintenance' : 'Mantenimiento', included)}
            </div>
          )}
        </div>

        {conditions && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#333', textTransform: 'uppercase', marginBottom: 6 }}>{lang === 'en' ? '* Conditions' : '* Condiciones'}</div>
            <div style={{ fontSize: 10, color: '#555', whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>{conditions}</div>
          </div>
        )}

        {notes && (
          <div style={{ marginBottom: 16, padding: 12, border: '1px solid #ddd', borderRadius: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#0e7490', textTransform: 'uppercase', marginBottom: 6 }}>{lang === 'en' ? 'Notes' : 'Observaciones'}</div>
            <div style={{ fontSize: 11, color: '#555', whiteSpace: 'pre-wrap' }}>{notes}</div>
          </div>
        )}

        {includeSignature && this.renderPrintSignatureBlock()}
      </div>
    );
  }

  renderPrintContent(t) {
    const { moduleMode } = this.state;
    return (
      <>
        {this.renderPrintCoverPage()}
        <div style={{ height: 32, margin: '24px 0', borderTop: '1px dashed #cbd5e1' }} />
        {this.renderPrintDataPage(t)}
        <div style={{ height: 32, margin: '24px 0', borderTop: '1px dashed #cbd5e1' }} />
        {this.renderPrintOfertaPage(t, moduleMode === 'list')}
        {moduleMode === 'visual' && (
          <>
            <div style={{ height: 32, margin: '24px 0', borderTop: '1px dashed #cbd5e1' }} />
            {this.renderPrintVisualPage()}
          </>
        )}
      </>
    );
  }

  render() {
    const { products } = this.props;
    if (!products) {
      return <div style={{ padding: 60, textAlign: 'center', color: TEXT_2 }}>Cargando catálogo...</div>;
    }

    const totals = this.calculateTotals();

    return (
      <div style={{ fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif", background: BG, minHeight: '100vh', padding: '24px 24px 60px' }}>
        {this.renderHero()}

        {this.state.showPreview ? (
          <div key="preview" style={{ margin: '0 auto' }}>
            <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${BORDER}`, padding: '30px 40px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
              {this.renderPrintContent(totals)}
            </div>
          </div>
        ) : (
          <div key="editor">
            {this.renderSummaryBanner(totals)}
            {this.renderInputSection()}
            {this.renderVisualSection()}
          </div>
        )}

        {/* Hidden print container for PDF export */}
        <div style={{ position: 'fixed', left: 0, top: 0, zIndex: -9999, opacity: 0, pointerEvents: 'none' }}>
          <div ref={this.printCoverRef} style={{ width: 720, background: '#fff', fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif" }}>
            {this.renderPrintCoverPage()}
          </div>
          <div ref={this.printVisualRef} style={{ width: 2800, background: '#fff', padding: '60px 80px', fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif" }}>
            {this.renderPrintVisualPage()}
          </div>
          <div ref={this.printDataRef} style={{ width: 720, background: '#fff', padding: '28px 32px', fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif" }}>
            {this.renderPrintDataPage(totals)}
          </div>
          <div ref={this.printOfertaRef} style={{ width: 720, background: '#fff', padding: '28px 32px', fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif" }}>
            {this.renderPrintOfertaPage(totals, this.state.moduleMode === 'list')}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  role:     state.auth.role,
  type:     state.auth.type,
  fullname: state.auth.fullname,
  authId:   state.auth.id,
  products: state.proposalCatalog.products,
  settings: state.proposalCatalog.settings,
  loading:  state.proposalCatalog.loading,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  fetchProposalProducts,
  fetchProposalSettings,
  seedProposalCatalog,
  createProposal,
  updateProposal,
}, dispatch);

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ProposalConfiguratorView));
